import moment from 'moment-timezone'
import { stopDate, stopTime, stopTimeZone } from 'utils/appointmentHelpers'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'

// TODO: Lack of proper types in this file is making this code brittle,
// need to move these methods into a separate Ts file and cleanup their usage as necessary

/** @deprecated */
export const appointmentToStopSummary = stop => ({
  city: capitalize(stop.city),
  state: stop.stateCode,
  zip: stop.zipcode,
  date: stopDate(stop.startAt, stop.timezone),
  dateFormatted: stopDate(stop.startAt, stop.timezone, FORMATS.dateFormat),
  dateTimestamp: moment.tz(stop.startAt, stop.timezone).format(FORMATS.dateFormat),
  shortDate: moment(stop.startAtDate, FORMATS.dateFormat),
  time: stopTime(stop.startAt, stop.endAt, stop.timezone, false, stop.startAtTime),
  timeTZ: stopTime(stop.startAt, stop.endAt, stop.timezone, true, stop.startAtTime),
  timezone: stopTimeZone(stop.startAt, stop.endAt, stop.timezone),
  status: stop.appointmentStatus,
  confirmedByName: stop.confirmedByName,
  appointmentTimeUpdatedByName: stop.appointmentTimeUpdatedByName,
  appointmentTimeUpdatedAt: stop.appointmentTimeUpdatedAt,
  schedulingOption: stop.schedulingOption,
  schedulingUrl: stop.schedulingUrl,
  autoAppointment: stop.autoAppointmentSchedule,
  facilityHasContacts: stop.facilityHasContacts,
  canAutoEmail: stop.canAutoEmail,
  smartAppointmentEligible: stop.smartAppointmentEligible || false,
})

/** @deprecated */
export const stopToStopSummary = ({ appointment, facility }) => ({
  city: capitalize(facility.city),
  state: facility.stateCode,
  zip: facility.zipcode,
  date: stopDate(appointment.startAt, appointment.timezone),
  dateFormatted: stopDate(appointment.startAt, appointment.timezone, FORMATS.dateFormat),
  dateTimestamp: moment.tz(appointment.startAt, appointment.timezone).format(FORMATS.dateFormat),
  shortDate: moment(appointment.startAtDate, FORMATS.dateFormat),
  time: stopTime(
    appointment.startAt,
    appointment.endAt,
    appointment.timezone,
    false,
    appointment.startAtTime
  ),
  timeTZ: stopTime(
    appointment.startAt,
    appointment.endAt,
    appointment.timezone,
    true,
    appointment.startAtTime
  ),
  timezone: stopTimeZone(appointment.startAt, appointment.endAt, appointment.timezone),
  status: appointment.appointmentStatus,
  smartAppointmentEligible: appointment.smartAppointmentEligible || false,
})

/** @deprecated */
export const stopsSummary = shipment => {
  if (!shipment) return {}
  const { stops, pickups = [], deliveries = [] } = shipment
  if (!stops) {
    const firstPickup = pickups[0] || {}
    const lastDelivery = deliveries[deliveries.length - 1] || {}
    return {
      pickup: appointmentToStopSummary(firstPickup),
      delivery: appointmentToStopSummary(lastDelivery),
    }
  }
  return {
    pickup: stopToStopSummary(stops[0] || {}),
    delivery: stopToStopSummary(stops[stops.length - 1] || {}),
  }
}

/** @deprecated */
const stopDetailsString = (stop, displaySequence) => {
  if (!stop || !stop.type) return ''
  const { appointment, facility } = stop
  const title = `${capitalize(stop.type)}${displaySequence ? ` ${stop.stopSequence}` : ''}`
  const location = `${capitalize(facility.city)}, ${facility.stateCode} - ${facility.zipcode}`
  const date = stopDate(appointment.startAt, appointment.timezone)
  const time = stopTime(
    appointment.startAt,
    appointment.endAt,
    appointment.timezone,
    true,
    appointment.startAtTime
  )
  return `${title}: ${location} - ${date} ${time}`
}

/** @deprecated */
export const shipmentDetailsString = shipment => {
  if (!shipment || !shipment.info) return ''
  const detailsArray = []
  const {
    stops = [],
    info: { totalMiles = 0, totalWeight = 0 } = {},
    equipment: { name: equipment } = {},
  } = shipment
  // default to minimum acceptable public weight
  let publicMinWeight = totalWeight
  if (totalWeight > 0 && totalWeight < 32000) publicMinWeight = 32000

  const [
    {
      info: { commodity },
    },
  ] = stops
  stops.forEach(s => {
    detailsArray.push(stopDetailsString(s, stops.length > 2))
  })
  detailsArray.push(commodity)
  detailsArray.push(`${publicMinWeight} lbs`)
  detailsArray.push(`${totalMiles} miles`)
  detailsArray.push(equipment)
  return detailsArray.join('\n')
}

/** @deprecated */
export const getNextStatus = (statusesArray, currentStatus) => {
  const index = statusesArray.findIndex(status => currentStatus.includes(status))
  if (index !== -1 && index < statusesArray.length - 1) {
    return statusesArray[index + 1]
  }
  return false
}

/** @deprecated */
export const displayDropTrailerStops = (stops = []) =>
  stops
    .filter(stop => stop.isDropTrailer)
    .map(stop => `${stop.type.charAt(0).toUpperCase()}${stop.stopSequence}`)
    .join(', ')

const formatSecurityRequirements = reqs =>
  reqs
    .map(({ key, label, value }) => {
      if (key === 'food_grade') return 'Food grade trailer'
      if (key === 'not_older_than') return `Trailer not older than ${value}`
      if (typeof value === 'boolean') {
        return label
      }
      if (Number(value) > 0) {
        return `${value} ${label}`
      }
      return value
    })
    .join(' · ')

const formatTemperatureRequirements = reqs =>
  !reqs.length
    ? undefined
    : {
        range: reqs
          .filter(({ key }) => key !== 'flow')
          .map(({ key, label, value }) => {
            if (key === 'fixed_temp_control') return `Fixed at ${value}°`
            return `${label} ${Number.isNaN(Number(value)) ? `: ${value}` : `${value}°`}`
          })
          .join(' · '),
        flow: reqs
          .filter(req => req.key === 'flow')
          .map(({ label, value }) => `${label}: ${value === "Cont'" ? 'Continuous' : value}`)
          .join(' · '),
      }

/** @deprecated */
export const processEquipmentAttributes = shipment => {
  const {
    equipment: { attributesRequirements: reqs = [] },
  } = shipment
  if (reqs.length) {
    const securityRequirements = reqs.filter(({ groupKey }) => groupKey !== 'temperature')
    const temperatureRequirements = reqs.filter(
      ({ groupKey, key }) => groupKey === 'temperature' && key !== 'downloadable_report'
    )
    shipment.equipment.attributesRequirementsSummary = {
      securityRequirements: formatSecurityRequirements(securityRequirements),
      temperatureRequirements: formatTemperatureRequirements(temperatureRequirements),
    }
  }
  return shipment
}
